import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Paper,
  useTheme,
  useMediaQuery
} from '@mui/material';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import consolidatedReportService from '../../services/consolidatedReportService';
import { ReporteConsolidado, FiltrosReporte } from '../../types/consolidatedReport.types';
import ReportFilters from './components/ReportFilters';
import ReportResults from './components/ReportResults';

const Reports: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { user } = useAuth();

  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reportData, setReportData] = useState<ReporteConsolidado | null>(null);
  const [currentFilters, setCurrentFilters] = useState<FiltrosReporte | null>(null);

  const handleGenerateReport = async (filters: FiltrosReporte) => {
    try {
      setLoading(true);
      setError(null);
      setReportData(null);

      const data = await consolidatedReportService.generateReport(filters);
      setReportData(data);
      setCurrentFilters(filters);
    } catch (err: any) {
      console.error('Error al generar reporte:', err);
      setError(err.error || 'Error al generar el reporte consolidado');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPDF = async () => {
    if (!currentFilters) return;

    try {
      setDownloading(true);
      setError(null);
      await consolidatedReportService.downloadPDF(currentFilters);
    } catch (err: any) {
      console.error('Error al descargar PDF:', err);
      setError(err.error || 'Error al descargar el PDF del reporte');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Box
      sx={{ 
        py: isMobile ? 2 : 4,
        px: isMobile ? 2 : 3,
        width: '100%',
        maxWidth: '100%',
        boxSizing: 'border-box',
        overflow: 'hidden'
      }}
    >
      {/* Encabezado */}
      <Box sx={{ mb: isMobile ? 2 : 4 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/dashboard')}
          sx={{ mb: 2 }}
          size={isMobile ? 'small' : 'medium'}
        >
          Volver al Dashboard
        </Button>

        <Typography variant={isMobile ? 'h5' : 'h4'} sx={{ fontWeight: 700, mb: 1 }}>
          Reportes Consolidados
        </Typography>

        <Typography variant="body2" color="text.secondary">
          Genera reportes consolidados de múltiples auditorías por negocio y periodo
        </Typography>
      </Box>

      {/* Mensaje de Error */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Filtros */}
      <ReportFilters
        onGenerate={handleGenerateReport}
        loading={loading}
        userRole={user?.role || ''}
      />

      {/* Estado de Carga */}
      {loading && (
        <Paper elevation={2} sx={{ p: 6, textAlign: 'center', mb: 3 }}>
          <CircularProgress size={60} sx={{ mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            Generando reporte...
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Esto puede tomar unos segundos
          </Typography>
        </Paper>
      )}

      {/* Resultados del Reporte */}
      {!loading && reportData && (
        <ReportResults 
          data={reportData} 
          onDownloadPDF={handleDownloadPDF}
          downloading={downloading}
        />
      )}

      {/* Estado Inicial */}
      {!loading && !reportData && !error && (
        <Paper elevation={2} sx={{ p: 6, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            Selecciona los filtros y genera un reporte
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Los reportes consolidados te permiten analizar el desempeño de múltiples auditorías en un periodo específico
          </Typography>
        </Paper>
      )}
    </Box>
  );
};

export default Reports;